import sensor_configuration_icon from "../../../../../../../assets/images/sensor_configuration_icon.png";
import { ConfigurationInputCard } from "./configuration-cards.component";
import type {
  DetectionRangeOption,
  SensorThresholdField,
  SensorValues,
} from "../../components/types";
import { clampValue, getMotionRangeLabel } from "src/shared/helpers/general-configuration.utils";

type SensorConfigurationSectionProps = {
  detectionRangeOptions: DetectionRangeOption[];
  sensorValues: SensorValues;
  onEnableDetectionRange: () => void;
  onDetectionRangeChange: (value: number) => void;
  onTogglePhotoSensing: (checked: boolean) => void;
  onThresholdChange: (field: SensorThresholdField, value: number | null) => void;
  onThresholdReset: (field: SensorThresholdField) => void;
  errors?: Record<string, string>;
};

export function SensorConfigurationSection({
  detectionRangeOptions,
  sensorValues,
  onEnableDetectionRange,
  onDetectionRangeChange,
  onTogglePhotoSensing,
  onThresholdChange,
  onThresholdReset,
  errors = {},
}: SensorConfigurationSectionProps) {
  const rangeMin =
    detectionRangeOptions?.length > 0
      ? Math.min(...detectionRangeOptions.map((opt) => opt.id))
      : 1;
  const rangeMax =
    detectionRangeOptions?.length > 0
      ? Math.max(...detectionRangeOptions.map((opt) => opt.id))
      : 3;

  const detectionRangeDisabled = sensorValues?.detectionRangeId == null;

  return (
    <aside className="configuration-card configuration-card--sensor">
      <div className="configuration-card__header configuration-card__header--stacked">
        <div>
          <div className="configuration-card__eyebrow">
            <img src={sensor_configuration_icon} alt="Sensor Configuration icon" className="me-2" />
            Sensor Configuration
          </div>
          <p className="configuration-card__subtitle">
            Configure environment sensing.
          </p>
        </div>
      </div>

      <div className="sensor-state-row">
        <span>Current State:</span>
        <span className="configuration-pill configuration-pill--soft">
          {getMotionRangeLabel(sensorValues.detectionRangeId, detectionRangeOptions)}
        </span>
      </div>

      <div
        className={`sensor-range-card ${detectionRangeDisabled ? "sensor-range-card--disabled" : ""}`}
        onClick={
          detectionRangeDisabled && detectionRangeOptions.length > 0
            ? onEnableDetectionRange
            : undefined
        }
      >
        <div className="sensor-range-card__icon">
          <svg
            width="20"
            height="20"
            viewBox="0 0 16 16"
            fill="currentColor"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M15.384 6.115a.485.485 0 0 0-.047-.736A12.444 12.444 0 0 0 8 3 12.444 12.444 0 0 0 .663 5.379a.485.485 0 0 0-.048.736.518.518 0 0 0 .668.05A11.448 11.448 0 0 1 8 4c2.507 0 4.827.802 6.716 2.164.205.148.49.13.668-.049Z" />
            <path d="M13.229 8.271a.482.482 0 0 0-.063-.745A9.455 9.455 0 0 0 8 6c-1.905 0-3.68.56-5.166 1.526a.48.48 0 0 0-.063.745.525.525 0 0 0 .652.065A8.46 8.46 0 0 1 8 7a8.46 8.46 0 0 1 4.577 1.336c.205.132.48.108.652-.065Z" />
            <path d="M11.42 10.376a.484.484 0 0 0-.086-.752A6.472 6.472 0 0 0 8 9c-1.226 0-2.37.328-3.334.974a.484.484 0 0 0-.086.752.51.51 0 0 0 .63.065A5.478 5.478 0 0 1 8 10c1.06 0 2.06.286 2.79.826c.199.148.469.157.63-.075Z" />
            <path d="M9.972 12.679a.483.483 0 0 0-.11-.759A3.485 3.485 0 0 0 8 11.5c-.606 0-1.283.196-1.862.42a.483.483 0 0 0-.111.76l1.611 1.611a.5.5 0 0 0 .708 0l1.626-1.612Z" />
          </svg>
        </div>
        <div className="sensor-range-card__body">
          <div className="sensor-range-card__title-row">
            <h3>Motion Detection Range</h3>
            <span className="configuration-pill configuration-pill--ghost">
              {getMotionRangeLabel(sensorValues?.detectionRangeId, detectionRangeOptions)}
            </span>
          </div>
          <input
            className="configuration-range"
            type="range"
            min={rangeMin}
            max={rangeMax}
            step={1}
            value={
              sensorValues?.detectionRangeId ||
              (detectionRangeOptions?.length > 0
                ? detectionRangeOptions[0]?.id
                : 1)
            }
            onChange={(event) => {
              if (detectionRangeDisabled) return;
              onDetectionRangeChange(
                clampValue(Number(event.target.value), rangeMin, rangeMax),
              );
            }}
            disabled={detectionRangeDisabled}
          />
          {/* <div className="sensor-range-card__ticks">
            {detectionRangeOptions.length > 0 ? (
              detectionRangeOptions.map((opt) => (
                <span key={opt.id}>{opt.name}</span>
              ))
            ) : (
              <>
                <span>Low</span>
                <span>Mid</span>
                <span>High</span>
              </>
            )}
          </div> */}
          <p className="sensor-range-card__hint">
            Drag slider to select detection range
          </p>
        </div>
      </div>

      <div className="sensor-toggle sensor-toggle--stacked">
        <div className="sensor-toggle__row">
          <div>
            <div className="sensor-toggle__title">Photo Sensing</div>
            <div className="sensor-toggle__subtitle">
              Adapt to ambient light
            </div>
          </div>
          <label className="switch">
            <input
              type="checkbox"
              checked={sensorValues?.isPhotoSensing || false}
              onChange={(event) => onTogglePhotoSensing(event?.target?.checked)}
            />
            <span className="switch__slider" />
          </label>
        </div>

        {sensorValues?.isPhotoSensing ? (
          <div className="sensor-thresholds">
            <ConfigurationInputCard
              title="Lower Threshold"
              subtitle="Triggers on dim daylight"
              value={sensorValues.lowerThreshold || 0}
              suffix="lux"
              min={0}
              max={100}
              step={1}
              disabled={
                !sensorValues?.isPhotoSensing || sensorValues?.lowerThreshold == null
              }
              onValueChange={(value) => onThresholdChange("lowerThreshold", value)}
              onClick={() => onThresholdReset("lowerThreshold")}
              error={errors.lowerThreshold}
            />
            <ConfigurationInputCard
              title="Upper Threshold"
              subtitle="Triggers on bright daylight"
              value={sensorValues.upperThreshold || 0}
              suffix="lux"
              min={0}
              max={100}
              step={1}
              disabled={
                !sensorValues?.isPhotoSensing || sensorValues.upperThreshold == null
              }
              onValueChange={(value) => onThresholdChange("upperThreshold", value)}
              onClick={() => onThresholdReset("upperThreshold")}
              error={errors.upperThreshold}
            />
          </div>
        ) : null}
      </div>
    </aside>
  );
}
